import { useQuery } from '@tanstack/react-query';
import { Slot } from 'expo-router';
import { useIsFocused } from 'expo-router/react-navigation';
import { useEffect, useRef } from 'react';

import { AskHeaderHost } from '@/components/libraryChat/AskHeader';
import { AskUiProvider } from '@/components/libraryChat/LibraryChatContext';
import { LibraryChatUnavailable } from '@/components/libraryChat/LibraryChatUnavailable';
import { ResearchTutorialProvider } from '@/components/libraryChat/researchTutorial/ResearchTutorial';
import { Spinner } from '@/components/ui/spinner';
import { View } from '@/components/ui/view';
import { useAskRealtime } from '@/hooks/ask/useAskRealtime';
import { useAskRefresh } from '@/hooks/ask/useAskRefresh';
import { useAbility } from '@/hooks/useAbility';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';

/**
 * Keeps the Ask screens current for as long as any of them is open. Mounted
 * inside the provider so the socket shares the one UI store the screens read,
 * and so the app's resume and the socket's resume share one refresh.
 */
function AskLiveUpdates() {
  const refresh = useAskRefresh();

  const isFocused = useIsFocused();

  const wasFocused = useRef(isFocused);

  useAskRealtime(refresh, { enabled: isFocused });

  /*
   * The socket was closed while Ask sat beneath another screen, so whatever
   * changed meanwhile was never announced. Coming back into view is a resume.
   */
  useEffect(() => {
    if (isFocused && !wasFocused.current) {
      refresh();
    }

    wasFocused.current = isFocused;
  }, [isFocused, refresh]);

  return null;
}

/**
 * The frame both Ask routes share: a new conversation at `/community/ask` and
 * a saved one at `/community/ask/[conversationId]`.
 *
 * It owns the UI store, the realtime subscription and the native header, so
 * the move from a new conversation to its saved address after the first turn
 * keeps the socket, the draft and the record of what this device submitted,
 * and the header does not rebuild. A `Slot`, not a nested
 * stack: the two routes are one workspace, and the move between them is a
 * replace, not a push.
 *
 * Reachability (verified NPI) is the secure layout's guard; this adds
 * the backend ability, which the secure layout cannot read because the ability
 * provider renders beneath its guards.
 *
 * The ability fails closed until the current user arrives, so "cannot" is only
 * believed once that query has data. Before then the spinner holds, rather than
 * flashing the unavailable state at a reader who is about to be let in. If that
 * query fails instead, the reader gets a retry: the spinner has no close
 * control of its own, so holding it would strand them.
 */
export default function AskLayout() {
  const { userId } = useAuthStatus();

  const {
    data: currentUser,
    isError,
    refetch,
  } = useQuery(getCurrentUserV2QueryOptions(userId ?? undefined));

  const ability = useAbility();

  if (!currentUser && isError) {
    return <LibraryChatUnavailable onRetry={() => refetch().catch(() => {})} />;
  }

  if (!currentUser) {
    return (
      <View className="flex-1 items-center justify-center bg-background-0">
        <Spinner />
      </View>
    );
  }

  if (!ability.can('read', 'AskConversation')) {
    return <LibraryChatUnavailable />;
  }

  return (
    <AskUiProvider>
      <AskLiveUpdates />

      <AskHeaderHost>
        <ResearchTutorialProvider>
          <Slot />
        </ResearchTutorialProvider>
      </AskHeaderHost>
    </AskUiProvider>
  );
}
